import { createApi } from "@reduxjs/toolkit/query/react";
import { LeadProvider, RegistrationRoleType, UserType } from "pages/registration/registrationService";
import { baseQueryWithAuth } from "services/common/rtkQueryConfig";

export const applicationApi = createApi({
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Application"],
  endpoints: (build) => ({
    getApplicationById: build.query<StepResponse, string>({
      query: (id) => `/application/${id}`,
      keepUnusedDataFor: 0,
    }),

    setApplicationStepAnswer: build.mutation<MutationResponse, Partial<StepAnswerRequest> & Pick<StepAnswerRequest, "id">>({
      query: ({ id, ...patch }) => ({
        url: `/application/${id}`,
        method: "PATCH",
        body: patch,
      }),
    }),
    setApplicationPreviousStep: build.mutation<StepResponse, Partial<StepAnswerRequest> & Pick<StepAnswerRequest, "id">>({
      query: ({ id, ...patch }) => ({
        url: `/application/${id}/previousStep`,
        method: "PATCH",
        body: patch,
      }),
    }),
    getLoanOfficerRatings: build.query<Ratings, string>({
      query: (id) => `/LoanOfficer/nhDealApplication-id/${id}/loanofficer-rating`,
    }),

    getloanOfficer: build.query<LoanOfficerDetail, string>({
      query: (id: string) => `/loanOfficer/nhDealApplication-id/${id}`,
    }),
    getOverallRatings: build.query<Ratings, void>({
      query: () => `/LoanOfficer/overall-rating`,
    }),
    getAddressSuggestions: build.query<AddressSuggestion[], string>({
      query: (inputData) => `/application/address/${inputData}`,
    }),
    getAddressZipCode: build.query<string, string>({
      query: (placeId) => `/application/address-zipcode/${placeId}`,
    }),
    getMyLoans: build.query<MyLoanInfo[], string>({
      query: (id) => `/dealapplication/user/${id}/my-loans`,
      keepUnusedDataFor: 0,
    }),
    getInquiryData: build.query<InquiryData, string>({
      query: (inquiryId) => `/application/inquiry/${inquiryId}/inquiry-data-model`,
      keepUnusedDataFor: 0,
    }),
    getMyLoansByDealApplicationId: build.query<MyLoanInfo[], { userId: string; nhDealApplicationId: string }>({
      query: ({ userId, nhDealApplicationId }) => `/dealapplication/user/${userId}/nhDealApplicationId/${nhDealApplicationId}/my-loans`,
    }),

    getInterimDashboard: build.query<boolean, { id: string; userId: string }>({
      query: ({ id, userId }) => `dealapplication/${id}/user/${userId}/interim-dashboard`,
      keepUnusedDataFor: 0,
    }),
    submitUqualReferalWithConsent: build.mutation<boolean, { nhDealApplicationId: string; prospectId: string }>({
      query: ({ nhDealApplicationId, prospectId }) => ({
        url: `/dealApplication/${nhDealApplicationId}/prospect/${prospectId}/uqual-referal-with-consent`,
        method: "POST",
      }),
    }),
    getAnalyticsEventDataByEventType: build.query<Record<string, any>, { applicationId: string; eventType: AnalyticsEvent }>({
      query: ({ applicationId, eventType }) => `/Application/${applicationId}/event-type/${eventType}/analytics-event`,
    }),
    updateStepAnswerCyberSecurityAuditLog: build.mutation<void, string>({
      query: (nhApplicationId) => ({
        url: `CyberSecurityAudit/application/${nhApplicationId}/update`,
        method: "POST",
      }),
    }),
    updateCreditConsent: build.mutation<void, { userId: string; nhDealApplicationId: string }>({
      query: ({ nhDealApplicationId, userId }) => ({
        url: `application/user-id/${userId}/nh-deal-application-id/${nhDealApplicationId}/credit-consent`,
        method: "POST",
      }),
    }),
    sendUserInteracted: build.mutation<void, { applicationId: string; prospectId: string; userType: UserType }>({
      query: ({ applicationId, prospectId, userType }) => ({
        url: "/user/interacted-event",
        method: "POST",
        body: {
          applicationId,
          prospectId,
          userType,
        },
      }),
    }),
  }),
});

export const {
  useGetApplicationByIdQuery,
  useSetApplicationStepAnswerMutation,
  useSetApplicationPreviousStepMutation,
  useGetLoanOfficerRatingsQuery,
  useGetAddressSuggestionsQuery,
  useGetAddressZipCodeQuery,
  useGetOverallRatingsQuery,
  useGetloanOfficerQuery,
  useGetMyLoansQuery,
  useGetInquiryDataQuery,
  useLazyGetMyLoansByDealApplicationIdQuery,
  useGetInterimDashboardQuery,
  useSubmitUqualReferalWithConsentMutation,
  useGetAnalyticsEventDataByEventTypeQuery,
  useUpdateStepAnswerCyberSecurityAuditLogMutation,
  useUpdateCreditConsentMutation,
  useSendUserInteractedMutation,
} = applicationApi;

export type StepActionResponse = "skip" | "regularAction" | "next" | "edit" | "remove" | "add" | "loop" | "otherButton" | "otherText";

export type AnalyticsEvent =
  | "FSLeadCompleted"
  | "FSProfileCompleted"
  | "FSPropertyCompleted"
  | "FSCreditCompleted"
  | "FSFinanceCompleted"
  | "FSDeclarationsCompleted";

export interface NextStepActionResponse {
  actionType: StepActionResponse;
  stepActionId: string;
  label: string;
  presentationOrder: number;
  hasPreviouslySelected?: boolean;
}
export interface MyLoanInfo {
  nhDealApplicationId: string;
  property: string;
  loanPurpose: string;
  loanAmount: string;
  status: ApplicationLoanStatus;
  startedDate: string;
  documentStatus?: boolean;
  nhApplicationId: string;
  applicationRoleType: string;
  dealApplicationId: string;
  nhApplicationStatus: string;
  leadProvider: LeadProvider;
  loanId?: string;
  hasPal?: boolean;
  prospectId?: string;
  isConsentCollected: boolean;
  userRole: RegistrationRoleType;
  inquiryId?: string;
  coBorrowerHasOwnNhApplication: boolean;
  nhApplicationPath: string;
  loanNumber: string;
  applicationNumber: string;
  userType: UserType;
  encompassLoanId?: string;
  dealId?: string;
}

export interface InquiryData {
  inquiryId: string;
  mappings: InquiryMapping[];
  leadProvider: string;
  leadCampaign: string;
  loanOfficerAssigned?: string;
  loanOfficerType?: string;
  creditScore?: string;
}
export interface InquiryMapping {
  stepCode: string;
  keyName: string;
  value: string | number | boolean;
}
export interface ConsentStatus {
  isTCPACollected: boolean;
  isEConsentCollected: boolean;
  isDataSharingCollected: boolean;
}

export type ApplicationLoanStatus =
  | "new"
  | "incomplete"
  | "started"
  | "processing"
  | "submittedToUnderwriting"
  | "conditionallyApproved"
  | "underReview"
  | "finalReview"
  | "clearedToClosed"
  | "funded"
  | "expired";

export interface InputResponse {
  badDataRegex: string;
  dataType: string;
  goodDataRegex: string;
  hideDataOnEntry: boolean;
  initialValue: string;
  hiddenValue: string;
  isHiddenInput: boolean;
  inputId: string;
  isOptional: boolean;
  label: string;
  maxDecimals: number;
  maxDigits: number;
  maxStringLength: number;
  minDecimals: number;
  minDigits: number;
  minStringLength: number;
  name: string;
  placeholder: string;
  description: string;
  properties: {
    options?: string[];
    purchasePropertyPrice?: number;
  };
  childInputs: InputResponse[];
}

type ActiveSummarySection = "profile" | "property" | "credit" | "finances" | "acknowledgements";

export type SectionType = "Profile" | "Property" | "Credit" | "Finances" | "Acknowledgements" | "Unknown";

export interface Section {
  sectionCode: string;
  sectionLabel: string;
  complete: boolean;
  progressWeight: number;
}

export interface StepResponse {
  stepId: string;
  label: string;
  subLabel?: string;
  nextStepActions: NextStepActionResponse[];
  skipAction?: NextStepActionResponse;
  stepType: string;
  inputs: InputResponse[];
  hasPreviousStep: boolean;
  blocks?: BlockResponse[];
  activeSummarySection?: ActiveSummarySection;
  section: SectionType;
  sections: Section[];
  stepCodeText: string;
  progress: number;
  texts: VisibleStepMetadata[];
  disclaimer?: StepDisclaimer;
  eventRequiringDataAnalytics?: string;
  isReadOnly?: boolean;
}

export interface StepDisclaimer {
  header: string;
  text: string;
}

export interface TableRowMetadata {
  columns: string[];
}

interface TableMetadata {
  rows: TableRowMetadata[];
}

export interface VisibleStepMetadata {
  header: string;
  text: string;
  table?: TableMetadata;
  headerType?: string;
  childItems?: VisibleStepMetadata[];
}

export interface MutationResponse {
  actionMessage?: string;
  validation: Validation;
  nextStep: StepResponse;
}

export interface Validation {
  success: boolean;
  message: string;
  inputErrors: InputError[];
}
interface InputError {
  inputId: string;
  name: string;
  errorMessage: string;
}

interface BlockResponse {
  label: string;
  description: string;
  dataItems: DataItemResponse[];
  blockType: BlockType;
  actions: NextStepActionResponse[];
}

export type BlockType =
  | "profileApplicant"
  | "profileSpouseCoApplicant"
  | "profileNonSpouseCoApplicant"
  | "creditApplicant"
  | "creditSpouseCoApplicant"
  | "propertyApplicant"
  | "propertyApplicantSpouse"
  | "financesApplicantIncome"
  | "financesSpouseCoApplicantIncome"
  | "financesApplicantAssets"
  | "financesSpouseCoApplicantAssets"
  | "financesAssets"
  | "acknowledgementsDemographics"
  | "acknowledgementsDeclarations";

interface DataItemResponse {
  label: string;
  fields: FieldResponse[];
  actions: NextStepActionResponse[];
  dataItemType: DataItemType;
}

type DataItemType =
  | "primaryAddress"
  | "mailingAddress"
  | "primaryBorrower"
  | "profileSpouse"
  | "coBorrower"
  | "primaryProperty"
  | "additionalProperty"
  | "electronicallyVerifiedAsset"
  | "borrowerDemographics"
  | "borrowerDeclarations"
  | "coBorrowerDemographics"
  | "coBorrowerDeclarations"
  | "electronicallyVerifiedIncome";

export interface AddressSuggestion {
  description: string;
  placeId: string;
}

export interface FieldResponse {
  displayName: string;
  value: string;
  actions: NextStepActionResponse[];
}

export interface SubmittedInputInfoRequest {
  inputId: string;
  submittedValue: string;
  name: string;
}

interface StepAnswerRequest {
  id: string;
  stepActionId?: string;
  stepActionValue?: string;
  answeredInputs?: SubmittedInputInfoRequest[];
}

export interface LoanOfficerDomain {
  domainName?: string;
}

export interface LoanOfficerDetail {
  name: string;
  fullName: string;
  nmlsNumber: string;
  email: string;
  phoneNumber: string;
  phones: {
    contactID: number;
    type: string;
    number: string;
    extension: string;
  }[];
  photoImageUrl: string;
  hasRatingInfo: boolean;
  averageRating: number;
  reviewCount: number;
  readMoreReviewsLink: string;
  workEmail: string;
  nmlsLicenseNumber: string;
  jobTitle: string;
  isLoUpdated: boolean;
  domains: LoanOfficerDomain[];
}

interface OverallRating {
  averageRating: number;
  reviewCount: number;
}

export interface Ratings {
  loanOfficerDetail?: LoanOfficerDetail;
  overallRating?: OverallRating;
}
